import {
  type PreparedShape,
  type Rng,
  type ShapeDefinition,
  type ShapeSink,
  type Vec3,
} from '../shape.js';
import { alongSegment, hash01, onDisc, onSphere, ramp, revolve, TAU } from './helpers.js';

/* ── Tree ──────────────────────────────────────────────────────────────── */

interface Branch {
  readonly from: Vec3;
  readonly to: Vec3;
  readonly radius: number;
  readonly level: number;
}

const MAX_BRANCH_LEVEL = 4;

function growChild(parent: Branch, random: Rng): Branch {
  const dx = parent.to.x - parent.from.x;
  const dy = parent.to.y - parent.from.y;
  const dz = parent.to.z - parent.from.z;
  const length = Math.max(1e-4, Math.hypot(dx, dy, dz));
  const spread = 0.5 + random() * 0.55;
  let nx = dx / length + (random() * 2 - 1) * spread;
  let ny = dy / length + 0.2 + random() * 0.3;
  let nz = dz / length + (random() * 2 - 1) * spread;
  const norm = Math.max(1e-4, Math.hypot(nx, ny, nz));
  nx /= norm;
  ny /= norm;
  nz /= norm;
  const childLength = length * (0.6 + random() * 0.25);
  return {
    from: parent.to,
    to: {
      x: parent.to.x + nx * childLength,
      y: parent.to.y + ny * childLength,
      z: parent.to.z + nz * childLength,
    },
    radius: parent.radius * 0.6,
    level: parent.level + 1,
  };
}

function buildBranches(random: Rng, variant: number): Branch[] {
  const trunk: Branch = {
    from: { x: 0, y: 0, z: 0 },
    to: { x: 0, y: 3 + variant * 1.6, z: 0 },
    radius: 0.46,
    level: 0,
  };
  const branches: Branch[] = [trunk];
  const pending: Branch[] = [trunk];
  while (pending.length > 0 && branches.length < 260) {
    const parent = pending.pop();
    if (parent === undefined || parent.level >= MAX_BRANCH_LEVEL) {
      continue;
    }
    const children = parent.level === 0 || random() < 0.35 ? 3 : 2;
    for (let i = 0; i < children; i += 1) {
      const child = growChild(parent, random);
      branches.push(child);
      pending.push(child);
    }
  }
  return branches;
}

export const tree: ShapeDefinition = {
  name: 'tree',
  palette: { base: 0x5a_3a_22, shade: 0x3c_94_45, accent: 0xdc_ff_9a },
  driftRate: 0.7,
  framing: 2.16,
  prepare: ({ random, total, variant }): PreparedShape => {
    const branches = buildBranches(random, variant);
    const count = branches.length;
    const tips = branches.filter((branch) => branch.level >= MAX_BRANCH_LEVEL - 1);
    const tipCount = Math.max(1, tips.length);
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.3) {
        const branch = branches[Math.floor(random() * count)];
        if (branch === undefined) {
          return;
        }
        alongSegment(sink, random, branch.from, branch.to, branch.radius);
        sink.tint = 0.02 + branch.level * 0.04 + random() * 0.05;
        sink.driftX = ramp(sink.y, 1, 8) * 0.28;
        sink.phase = branch.level * 0.9;
      } else if (p < 0.86) {
        // Foliage: soft clusters gathered on the outer branch tips.
        const tip = tips[Math.floor(random() * tipCount)];
        if (tip === undefined) {
          return;
        }
        onSphere(sink, random, 0.5 + random() * 0.75);
        sink.x += tip.to.x;
        sink.y += tip.to.y;
        sink.z += tip.to.z;
        sink.tint = 0.4 + random() * 0.28;
        // Leaves further out move more — the whole canopy breathes.
        sink.driftX = 0.34;
        sink.driftZ = 0.22;
        sink.phase = tip.to.x * 0.5 + tip.to.z * 0.3;
      } else if (p < 0.95) {
        // Grass and roots at the base.
        onDisc(sink, random, 11);
        const r = Math.hypot(sink.x, sink.z);
        sink.y = random() * (0.35 + ramp(3 - r, 0, 3) * 0.5);
        sink.tint = 0.32 + random() * 0.16;
        sink.driftX = 0.1;
        sink.phase = r * 0.8;
      } else {
        // A few leaves let go and turning in the air.
        onDisc(sink, random, 10);
        sink.y = 0.3 + random() * 7;
        sink.tint = 0.9 + random() * 0.1;
        sink.driftX = 0.8;
        sink.driftY = 0.5;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Flower ────────────────────────────────────────────────────────────── */

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

function petalPoint(sink: ShapeSink, random: Rng, angle: number, reach: number): void {
  const along = random();
  // A petal is widest a third of the way out and curls upward at the tip.
  const width = Math.sin(along * Math.PI) ** 0.7 * 0.75;
  const across = (random() * 2 - 1) * width;
  const r = 0.9 + along * reach;
  const lift = along * along * 1.5 - Math.abs(across) * 0.35;
  sink.x = Math.cos(angle) * r - Math.sin(angle) * across;
  sink.z = Math.sin(angle) * r + Math.cos(angle) * across;
  sink.y = 6 + lift;
}

export const flower: ShapeDefinition = {
  name: 'flower',
  palette: { base: 0x2e_7a_38, shade: 0xe8_4a_86, accent: 0xff_ee_86 },
  driftRate: 0.8,
  framing: 2.84,
  prepare: ({ random, total, variant }): PreparedShape => {
    const petals = 8 + Math.floor(variant * 8);
    const reach = 2.6 + variant * 1.4;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.52) {
        petalPoint(sink, random, Math.floor(random() * petals) * (TAU / petals), reach);
        sink.tint = 0.35 + random() * 0.3;
        sink.driftX = 0.16;
        sink.driftY = 0.1;
      } else if (p < 0.68) {
        // Seed head: real phyllotaxis, which is what makes it read as botanical.
        const n = Math.floor(random() * 420);
        const r = Math.sqrt(n / 420) * 0.95;
        const angle = n * GOLDEN_ANGLE;
        sink.x = Math.cos(angle) * r;
        sink.z = Math.sin(angle) * r;
        sink.y = 6.1 + (0.95 - r) * 0.55;
        sink.tint = 0.88 + random() * 0.12;
      } else if (p < 0.84) {
        // Stem, with a gentle bend.
        const v = random();
        sink.x = Math.sin(v * 1.3) * 0.5;
        sink.y = v * 6;
        sink.z = (random() - 0.5) * 0.16;
        sink.x += (random() - 0.5) * 0.16;
        sink.tint = 0.05 + random() * 0.08;
        sink.driftX = v * v * 0.3;
      } else if (p < 0.96) {
        // Two long leaves.
        const side = random() < 0.5 ? -1 : 1;
        const along = random();
        const blade = Math.sin(along * Math.PI) * 0.6;
        sink.x = side * along * 2.6 + Math.sin(along * 2.2) * 0.2;
        sink.y = 1.6 + along * 1.8 - along * along * 1.4;
        sink.z = (random() * 2 - 1) * blade;
        sink.tint = 0.12 + random() * 0.1;
        sink.driftZ = along * 0.3;
      } else {
        // Pollen in the air.
        onSphere(sink, random, 1.6 + random() * 2.4);
        sink.y += 6.2;
        sink.tint = 0.95;
        sink.driftY = 0.4;
        sink.driftX = 0.4;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Jellyfish ─────────────────────────────────────────────────────────── */

const BELL_HEIGHT = 3.4;

export const jellyfish: ShapeDefinition = {
  name: 'jellyfish',
  palette: { base: 0x01_06_1e, shade: 0x00_77_ff, accent: 0x00_ff_ee },
  driftRate: 0.85,
  framing: 2.7,
  prepare: ({ random, total, variant }): PreparedShape => {
    const strands = 32 + Math.floor(variant * 24);
    const bellRadius = 3.2 + variant;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.44) {
        // The bell: a dome that pulses open and shut with solid plasma skin.
        const shell = random() > 0.35 ? 1 : 0.94 + random() * 0.05;
        revolve(
          sink,
          random,
          (v) => Math.sin((1 - v) * Math.PI * 0.5) * bellRadius * shell,
          BELL_HEIGHT,
        );
        sink.y += 4;
        const rim = ramp(1 - sink.y / (4 + BELL_HEIGHT), 0.55, 1);
        sink.tint = 0.28 + rim * 0.4;
        sink.driftX = sink.x * 0.14;
        sink.driftZ = sink.z * 0.14;
        sink.driftY = -0.3;
        sink.phase = (sink.y - 4) * 0.3;
      } else if (p < 0.5) {
        // Solid luminous rim defining the bell edge
        const angle = random() * TAU;
        const bleed = (random() - 0.5) * 0.4;
        const r = bellRadius + bleed;
        sink.x = Math.cos(angle) * r;
        sink.z = Math.sin(angle) * r;
        sink.y = 4 + (random() - 0.5) * 0.35;
        sink.tint = 0.75 + random() * 0.22;
        sink.driftX = sink.x * 0.14;
        sink.driftZ = sink.z * 0.14;
        sink.driftY = -0.18;
        sink.phase = random() * 0.4;
      } else if (p < 0.68) {
        // Frilled oral arms anchored to the bell underside
        const angle = random() * TAU;
        const down = random();
        const armRootRadius = bellRadius * 0.4;
        const wave = Math.sin(down * 5.5 + angle * 3.5) * down * 0.6;
        const r = armRootRadius + wave;
        sink.x = Math.cos(angle) * r;
        sink.z = Math.sin(angle) * r;
        sink.y = 4 - down * 4.8;
        sink.tint = 0.38 + down * 0.4;

        const baseDriftX = sink.x * 0.14;
        const baseDriftZ = sink.z * 0.14;
        const baseDriftY = -0.3;
        const armPhase = angle * 2.5 + down * 4.5;

        sink.driftX = baseDriftX * (1 - down) + (baseDriftX + Math.cos(armPhase) * 0.65) * down;
        sink.driftZ = baseDriftZ * (1 - down) + (baseDriftZ + Math.sin(armPhase) * 0.65) * down;
        sink.driftY = baseDriftY * (1 - down) + (baseDriftY - down * 0.35) * down;
        sink.phase = armPhase * down;
      } else if (p < 0.86) {
        // Long trailing tentacles anchored to the bell rim and moving independently
        const strand = Math.floor(random() * strands);
        const angle = (strand / strands) * TAU;
        const down = random();
        const strandPhase = strand * 2.3999 + strand * 1.618;
        const freq1 = 5.2 + (strand % 5) * 0.75;
        const freq2 = 4.1 + (strand % 4) * 0.85;

        const curlX =
          Math.sin(down * freq1 + strandPhase) * down * (1.1 + Math.sin(strand * 1.7) * 0.35);
        const curlZ =
          Math.cos(down * freq2 + strandPhase * 1.25) *
          down *
          (1.1 + Math.cos(strand * 2.3) * 0.35);

        sink.x = Math.cos(angle) * bellRadius + curlX;
        sink.z = Math.sin(angle) * bellRadius + curlZ;
        sink.y = 4 - down * 13;
        sink.tint = 0.58 + down * 0.4 + Math.sin(down * 10 + strandPhase) * 0.05;

        const baseDriftX = Math.cos(angle) * bellRadius * 0.14;
        const baseDriftZ = Math.sin(angle) * bellRadius * 0.14;
        const baseDriftY = -0.3;

        const waveX = Math.sin(down * freq1 + strandPhase) * 1.2;
        const waveZ = Math.cos(down * freq2 + strandPhase * 1.3) * 1.2;
        const waveY = Math.sin(down * 3.8 + strandPhase * 0.8) * 0.5 - 0.4;

        sink.driftX = baseDriftX * (1 - down) + (baseDriftX + waveX) * down;
        sink.driftZ = baseDriftZ * (1 - down) + (baseDriftZ + waveZ) * down;
        sink.driftY = baseDriftY * (1 - down) + (baseDriftY + waveY) * down;
        sink.phase = (strandPhase + down * 4.5) * down;
      } else {
        // Billowing ethereal plasma smoke plumes streaming out of the jellyfish
        const u = random();
        const angle = random() * TAU;
        const plumeSpread = Math.sin(u * Math.PI) * (1.2 + random() * 1.8);
        const r = (bellRadius * 0.7 + plumeSpread) * (0.8 + u * 1.1);
        const vortexAngle = angle + u * 3.5;
        sink.x = Math.cos(vortexAngle) * r;
        sink.z = Math.sin(vortexAngle) * r;
        sink.y = 4 + u * (BELL_HEIGHT * 2.2) - (random() - 0.5) * 1.5;
        sink.tint = 0.72 + random() * 0.28;

        const swirl = 0.7 + u * 0.9;
        sink.driftX = Math.cos(vortexAngle + 1.57) * swirl + Math.sin(u * 8) * 0.4;
        sink.driftZ = Math.sin(vortexAngle + 1.57) * swirl + Math.cos(u * 8) * 0.4;
        sink.driftY = 0.65 + u * 0.8;
        sink.phase = vortexAngle + u * 5;
      }
    };
    return { place };
  },
};
